/*
 *
 * task.js
 *
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import taskAPI from "../../api/taskAPI";
import moment from "moment";

import thunk from "../../helpers/thunk";
import { fetchGlossariesByTask } from "./glossary";
import { fetchMemoriesByTask } from "./memory";

const fetchTask = thunk("task/fetchTask", taskAPI.fetchByID, (o) => o.r.data);

const fetchContractorTasks = thunk(
  "task/fetchContractorTasks",
  taskAPI.fetchAll,
  (o) => ({
    data: o.r.data,
    userID: o.getState().session.userID,
  }),
);

const createTask = thunk("task/createTask", taskAPI.create, (o) => o.r.data);

//const splitTask = thunk("task/splitTask", taskAPI.split, (o) => o.r.data);

const splitTask = thunk("task/splitTask", taskAPI.split, (o) => {
  console.log("split API response:", o.r.data); // 👈 check this
  return o.r.data;
});
const deleteTasks = thunk("task/deleteTasks", taskAPI.delete, (o) => ({
  IDs: o.d.taskIDs,
  projectID: o.d.projectID,
}));

const assignTasksTranslator = thunk(
  "task/assignTasksTranslator",
  taskAPI.assignTranslator,
  (o) => ({
    ...o.d,
    userName: o.r.data.userName,
  }),
);

const assignTasksEditor = thunk(
  "task/assignTasksEditor",
  taskAPI.assignEditor,
  (o) => ({
    ...o.d,
    userName: o.r.data.userName,
  }),
);

const setTasksDue = thunk("task/setTasksDue", taskAPI.setDue, (o) => ({
  ...o.d,
}));

const setTasksStatus = thunk("task/setTasksStatus", taskAPI.setStatus, (o) => ({
  ...o.d,
}));

const finishTask = thunk("task/finishTask", taskAPI.finish, (o) => ({
  ...o.d,
  ...o.r.data,
}));

const fetchTasksByProject = thunk(
  "task/fetchTasksByProject",
  taskAPI.fetchByProject,
  (o) => ({
    tasks: o.r.data,
    projectID: o.d.projectID,
  }),
);

const deleteTranslations = thunk(
  "task/deleteTranslations",
  taskAPI.deleteTranslations,
  (o) => ({
    ...o.d,
  }),
);

const saveTask = createAsyncThunk(
  "task/saveTask",
  async (data, { rejectWithValue, getState }) => {
    const id = [];
    const target = [];
    const completed = [];

    const task = getState().task.tasks[data.taskID];

    Object.keys(task.rows).forEach((rowID) => {
      const row = task.rows[rowID];

      if (row.modified) {
        id.push(rowID);
        target.push(row.target);
        completed.push(row.completed);
      }
    });

    return await taskAPI
      .save(data.taskID, id, target, completed)
      .then((r) => {
        return {
          taskID: data.taskID,
          rowIDs: id,
        };
      })
      .catch((e) => rejectWithValue());
  },
);

const fetchSuggestions = thunk(
  "task/fetchSuggestions",
  taskAPI.fetchSuggestions,
  (o) => ({
    taskID: o.d.taskID, // from the dispatch args
    rowID: o.d.rowID,
    ...o.r.data, // glossary, memory, etc.
  }),
);

const preTranslate = thunk("task/preTranslate", taskAPI.preTranslate, (o) => ({
  data: o.r.data,
}));

const saveTaskRowComment = thunk(
  "task/saveTaskRowComment",
  taskAPI.saveRowComment,
  (o) => ({
    ...o.d,
  }),
);

const fetchTaskRowComment = thunk(
  "task/fetchTaskRowComment",
  taskAPI.fetchRowComment,
  (o) => ({
    rowID: o.d.rowID,
    taskID: o.d.taskID,
    comment: o.r.data.comment,
  }),
);

const fetchTaskRowContextNotes = thunk(
  "task/fetchTaskRowContextNotes",
  taskAPI.fetchRowContextNotes,
  (o) => ({
    rowID: o.d.rowID,
    taskID: o.d.taskID,
    notes: o.r.data.notes,
  }),
);

const splitTaskRowGender = thunk(
  "task/splitGender",
  taskAPI.splitGender,
  (o) => ({
    rowID: o.d.rowID,
    taskID: o.d.taskID,
    ...o.r.data,
  }),
);

/* slice */
const slice = createSlice({
  name: "task",
  initialState: {
    tasks: {},
  },
  reducers: {
    rowModified: (state, action) => {
      const taskID = action.payload.taskID;
      const rowID = action.payload.rowID;
      const target = action.payload.target;
      const completed = action.payload.completed;

      const row = state.tasks[taskID].rows[rowID];
      state.tasks[taskID].isSaved = false;

      row.modified = true;

      if (target != undefined) row.target = target;
      if (completed != undefined) row.completed = completed ? 1 : 0;
    },
    taskRowSelected: (state, action) => {
      const taskID = action.payload.taskID;
      const rowID = action.payload.rowID;

      state.tasks[taskID].selectedRow = rowID;
    },
    rowCommentModified: (state, action) => {
      const taskID = action.payload.taskID;
      const rowID = action.payload.rowID;
      const comment = action.payload.comment;
      const row = state.tasks[taskID].rows[rowID];
      row.comment = {
        modified: true,
        text: comment,
      };
    },
  },
  extraReducers: {
    [createTask.fulfilled]: (state, action) => {
      const tasks = action.payload;
      tasks.forEach((task) => {
        state.tasks[task.ID] = {
          ...task,
        };
        delete state.tasks[task.ID].ID;
      });
    },
    [deleteTasks.fulfilled]: (state, action) => {
      const deletedIDs = action.payload.IDs;
      deletedIDs.forEach((ID) => {
        delete state.tasks[ID];
      });
    },
    [assignTasksTranslator.fulfilled]: (state, action) => {
      const assignedIDs = action.payload.taskIDs;
      const assignedTo = action.payload.userName;
      assignedIDs.forEach((ID) => {
        state.tasks[ID].translator = assignedTo;
      });
    },
    [assignTasksEditor.fulfilled]: (state, action) => {
      const assignedIDs = action.payload.taskIDs;
      const assignedTo = action.payload.userName;
      assignedIDs.forEach((ID) => {
        state.tasks[ID].editor = assignedTo;
      });
    },
    [fetchTasksByProject.fulfilled]: (state, action) => {
      const tasks = action.payload.tasks;

      tasks.forEach((task) => {
        state.tasks[task.ID] = {
          ...task,
        };
        delete state.tasks[task.ID].ID;
      });
    },
    [fetchGlossariesByTask.fulfilled]: (state, action) => {
      const glossaries = action.payload.data;
      const taskID = action.payload.taskID;

      if (!state.tasks[taskID]) state.tasks[taskID] = {};
      state.tasks[taskID].glossaries = [];

      glossaries.forEach((glossary) => {
        state.tasks[taskID].glossaries.push(glossary.ID);
      });
    },
    [fetchMemoriesByTask.fulfilled]: (state, action) => {
      const memories = action.payload.data;
      const taskID = action.payload.taskID;

      if (!state.tasks[taskID]) state.tasks[taskID] = {};
      state.tasks[taskID].memories = [];

      memories.forEach((memory) => {
        state.tasks[taskID].memories.push(memory.ID);
      });
    },
    [splitTask.fulfilled]: (state, action) => {
      const tasks = action.payload;

      tasks.forEach((task) => {
        state.tasks[task.ID] = {
          ...task,
        };
        delete state.tasks[task.ID].ID;
      });
    },
    [fetchTask.fulfilled]: (state, action) => {
      const task = action.payload;
      state.tasks[task.ID] = {
        ...task,
      };

      state.tasks[task.ID].rows = {};

      const rowIDs = task.rows.ID;
      const rowSources = task.rows.source;
      const rowTargets = task.rows.target;
      const rowCompleteds = task.rows.completed;
      const charLimits = task.rows.charLimit;
      const contextKeys = task.rows.contextKey;
      const genders = task.rows.gender;
      const sortNums = task.rows.sortNum;

      rowIDs.forEach((rowID, index) => {
        state.tasks[task.ID].rows[rowID] = {
          source: rowSources[index],
          target: rowTargets[index],
          completed: rowCompleteds[index],
          charLimit: charLimits[index],
          contextKey: contextKeys[index],
          gender: genders[index],
          sortNum: sortNums[index],
        };
      });

      state.tasks[task.ID].selectedRow = rowIDs[0];

      delete state.tasks[task.ID].ID;
    },
    [saveTask.fulfilled]: (state, action) => {
      const savedRowIDs = action.payload.rowIDs;
      const taskID = action.payload.taskID;

      savedRowIDs.forEach((rowID) => {
        state.tasks[taskID].rows[rowID].modified = false;
      });

      state.tasks[taskID].isSaved = true;
    },
    [fetchSuggestions.pending]: (state, action) => {
      Object.keys(state.tasks).forEach((ID) => {
        delete state.tasks[ID].suggestions;
        state.tasks[ID].suggestions = [];
      });
    },
    [fetchSuggestions.fulfilled]: (state, action) => {
      const taskID = action.payload.taskID;
      const task = state.tasks[taskID];

      task.suggestions = {};

      task.suggestions.glossary = action.payload.glossary;
      task.suggestions.memory = action.payload.memory;
    },
    [setTasksDue.fulfilled]: (state, action) => {
      const taskIDs = action.payload.taskIDs;
      const type = action.payload.type;
      const date = action.payload.date;
      taskIDs.forEach((ID) => {
        if (type == "t")
          state.tasks[ID].tDue = moment(date).format("MM/DD/YYYY");
        if (type == "e")
          state.tasks[ID].eDue = moment(date).format("MM/DD/YYYY");
      });
    },
    [fetchContractorTasks.fulfilled]: (state, action) => {
      const tTasks = action.payload.data.t;
      const eTasks = action.payload.data.e;

      tTasks.forEach((task) => {
        state.tasks[task.id] = {
          ...task,
        };
        delete state.tasks[task.id].ID;
      });

      eTasks.forEach((task) => {
        state.tasks[task.id] = {
          ...task,
        };
        delete state.tasks[task.id].ID;
      });
    },
    [finishTask.fulfilled]: (state, action) => {
      const taskID = action.payload.taskID;
      const status = action.payload.status;

      state.tasks[taskID].status = status;
    },
    [setTasksStatus.fulfilled]: (state, action) => {
      const taskIDs = action.payload.taskIDs;
      const status = action.payload.status;

      taskIDs.forEach((taskID) => (state.tasks[taskID].status = status));
    },
    [fetchTaskRowComment.fulfilled]: (state, action) => {
      const rowID = action.payload.rowID;
      const taskID = action.payload.taskID;
      const comment = action.payload.comment;

      state.tasks[taskID].rows[rowID].comment = {
        text: comment,
      };
    },
    [fetchTaskRowContextNotes.fulfilled]: (state, action) => {
      const rowID = action.payload.rowID;
      const taskID = action.payload.taskID;
      const notes = action.payload.notes;

      state.tasks[taskID].rows[rowID].notes = notes;
    },
    [saveTaskRowComment.fulfilled]: (state, action) => {
      const rowID = action.payload.rowID;
      const taskID = action.payload.taskID;

      delete state.tasks[taskID].rows[rowID].comment.modified;
    },
    [splitTaskRowGender.fulfilled]: (state, action) => {
      const splitRowID = action.payload.rowID;
      const taskID = action.payload.taskID;

      const femaleID = action.payload.ID;
      const femaleSource = action.payload.source;
      const femaleTarget = action.payload.target;
      const femaleCompleted = action.payload.completed;
      const femaleCharLimit = action.payload.charLimit;
      const femaleContextKey = action.payload.contextKey;
      const femaleGender = action.payload.gender;

      state.tasks[taskID].rows[splitRowID].gender = "M";

      state.tasks[taskID].rows[femaleID] = {
        source: femaleSource,
        target: femaleTarget,
        completed: femaleCompleted,
        charLimit: femaleCharLimit,
        contextKey: femaleContextKey,
        gender: femaleGender,
        sortNum: state.tasks[taskID].rows[splitRowID].sortNum + ".5",
      };
    },
    [deleteTranslations.fulfilled]: (state, action) => {
      const taskID = action.payload.taskID;

      delete state.tasks[taskID];
    },
  },
});

export {
  fetchTask,
  fetchTasksByProject,
  createTask,
  splitTask,
  deleteTasks,
  assignTasksTranslator,
  assignTasksEditor,
  saveTask,
  fetchSuggestions,
  setTasksDue,
  setTasksStatus,
  fetchContractorTasks,
  preTranslate,
  finishTask,
  saveTaskRowComment,
  fetchTaskRowComment,
  splitTaskRowGender,
  fetchTaskRowContextNotes,
  deleteTranslations,
};

export const { rowModified, taskRowSelected, rowCommentModified } =
  slice.actions;

export default slice.reducer;
